import type { SortingState } from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { STICKY_TABLE_HEADER_CLASSES } from '~/components/ScrollCollapse/constants/classes';
import { cn } from '~/lib/cn';
import { DataUnavailable } from '~/modules/artist-roster/components/DataUnavailable/DataUnavailable';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/modules/artist-roster/components/Table';
import { SkeletonTable } from '~/modules/artist-roster/components/Table/SkeletonTable';
import { TableHeaderBackground } from '~/modules/artist-roster/components/Table/TableHeaderBackground';

import { useBenchmarkColumns } from '../hooks/useBenchmarkColumns';
import type { BenchmarkTableMetaType, CampaignBenchmark } from '../lib/types';

interface CampaignBenchmarksTableProps {
  data: CampaignBenchmark[];
  error?: Error | null;
  isLoadingBenchmarks: boolean;
  isFetchingBenchmarks: boolean;
  objective?: string;
  className?: string;
}

export function CampaignBenchmarksTable({
  data,
  error,
  isFetchingBenchmarks,
  objective,
  className,
}: CampaignBenchmarksTableProps) {
  const { t } = useTranslation('campaign-planner', { keyPrefix: 'benchmarks' });
  const [sorting, setSorting] = useState<SortingState>([
    {
      desc: false,
      id: 'market',
    },
  ]);

  const { columns } = useBenchmarkColumns();

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    enableSortingRemoval: true,
  });

  if (error) {
    return (
      <DataUnavailable
        title={t('errors.generic')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  let body;

  if (isFetchingBenchmarks) {
    body = (
      <SkeletonTable
        size={4}
        headers={table.getHeaderGroups()[0].headers}
        cellClassName="h-[5rem]"
        firstLineClassName="w-4xl"
        secondLineClassName="w-3xl"
      />
    );
  } else if (table.getRowModel().rows?.length) {
    body = table.getRowModel().rows.map((row, rowIndex) => (
      <TableRow
        aria-label={`Row for ${row.original?.market || 'benchmark'}`}
        key={row.id}
        data-state={row.getIsSelected() && 'selected'}
        className="group relative duration-300"
      >
        {row.getVisibleCells().map((cell) => {
          const columnMeta = cell.column.columnDef
            .meta as BenchmarkTableMetaType;

          return (
            <TableCell
              key={cell.id}
              className={cn(
                'border-y p-0 border-transparent',
                'first:rounded-l-sm first:border-l',
                'last:rounded-r-sm last:border-r',
                rowIndex % 2 === 0 && 'bg-default-inverse/default',
                columnMeta?.className,
              )}
            >
              {cell.column.id === 'market' ? (
                <div className="flex items-center gap-sm py-md pl-lg">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              ) : (
                flexRender(cell.column.columnDef.cell, cell.getContext())
              )}
            </TableCell>
          );
        })}
      </TableRow>
    ));
  } else {
    body = (
      <TableRow>
        <TableCell colSpan={table.getAllColumns().length}>
          <DataUnavailable
            title={objective ? t('noData.title') : t('initial.title')}
            subtitle={objective ? t('noData.caption') : t('initial.caption')}
            icon={
              <Icon
                type="list-music"
                size="4x"
                variant="light"
                aria-hidden
                className="text-default"
              />
            }
          />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <Table
        aria-label={t('labels.benchmarksList')}
        className={cn('w-full flex-1 table-fixed text-left', className)}
      >
        <TableHeader className={STICKY_TABLE_HEADER_CLASSES}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                const columnMeta = header.column.columnDef
                  .meta as BenchmarkTableMetaType;

                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'text-body-sm font-normal first:pl-lg',
                      columnMeta?.className,
                    )}
                  >
                    {!index ? <TableHeaderBackground /> : null}
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>{body}</TableBody>
      </Table>
    </>
  );
}

CampaignBenchmarksTable.displayName = 'CampaignBenchmarksTable';
